import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { BayenahLogo } from '@/components/brand/BayenahLogo';

const getPasswordStrength = (password) => {
  if (!password) return { level: 0, label: '', color: '' };
  let score = 0;
  if (password.length >= 6) score++;
  if (password.length >= 10) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  if (score <= 1) return { level: 1, label: 'ضعيفة', color: 'bg-red-500' };
  if (score <= 2) return { level: 2, label: 'متوسطة', color: 'bg-yellow-500' };
  if (score <= 3) return { level: 3, label: 'جيدة', color: 'bg-blue-500' };
  return { level: 4, label: 'قوية', color: 'bg-green-500' };
};

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validSession, setValidSession] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    supabase?.auth.getSession().then(({ data: { session } }) => {
      setValidSession(!!session);
      if (!session) {
        setError('رابط إعادة تعيين كلمة المرور غير صالح أو منتهي الصلاحية');
      }
    });
  }, []);

  const strength = getPasswordStrength(password);

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('كلمتا المرور غير متطابقتين');
      return;
    }
    if (password.length < 6) {
      setError('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (err) {
      const map = {
        'New password should be different from the old password': 'كلمة المرور الجديدة يجب أن تختلف عن القديمة',
        'Auth session missing': 'انتهت صلاحية الجلسة',
      };
      setError(map[err.message] || err.message);
      return;
    }
    setSuccess(true);
    setTimeout(() => navigate('/chat'), 2000);
  };

  if (validSession === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F5]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a1a1a]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F7F5] p-4" dir="rtl">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-[#E8E8E8] p-8">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <BayenahLogo height={36} variant="dark" />
          </div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">إعادة تعيين كلمة المرور</h1>
          <p className="text-[#6B6B6B] mt-2">أدخل كلمة المرور الجديدة</p>
        </div>

        {success ? (
          <div className="text-center py-8" data-testid="reset-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-green-700 mb-2">تم بنجاح!</h3>
            <p className="text-[#6B6B6B]">تم تغيير كلمة المرور. جاري تحويلك...</p>
          </div>
        ) : (
          <form onSubmit={handleReset} data-testid="reset-password-form">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4 flex items-center gap-2" data-testid="reset-error">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {!validSession ? (
              <div className="text-center py-4">
                <p className="text-[#6B6B6B] mb-4">الرابط غير صالح أو منتهي الصلاحية.</p>
                <button type="button" onClick={() => navigate('/')} className="text-[#1a1a1a] font-medium hover:underline">
                  العودة للصفحة الرئيسية
                </button>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">كلمة المرور الجديدة</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full p-3 border border-[#E8E8E8] rounded-lg bg-white text-[#1A1A1A] pr-4 pl-12 focus:outline-none focus:ring-2 focus:ring-[#1a1a1a]/20"
                      placeholder="6 أحرف على الأقل"
                      required
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B] hover:text-[#1A1A1A]"
                      tabIndex={-1}
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {password && (
                    <div className="mt-2" data-testid="password-strength">
                      <div className="flex gap-1 h-1">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className={`flex-1 rounded transition-colors ${i <= strength.level ? strength.color : 'bg-gray-200'}`} />
                        ))}
                      </div>
                      <p className="text-xs mt-1 text-[#6B6B6B]">{strength.label}</p>
                    </div>
                  )}
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">تأكيد كلمة المرور</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full p-3 border border-[#E8E8E8] rounded-lg bg-white text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#1a1a1a]/20"
                    placeholder="أعد كتابة كلمة المرور"
                    required
                    data-testid="confirm-password-input"
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-red-500 mt-1">كلمتا المرور غير متطابقتين</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !password || password !== confirmPassword}
                  className="w-full bg-[#1a1a1a] text-white p-3 rounded-lg hover:bg-[#333] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                  data-testid="save-password-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>جاري الحفظ...</span>
                    </>
                  ) : (
                    <span>حفظ كلمة المرور الجديدة</span>
                  )}
                </button>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
